import { getAuth } from 'firebase/auth';
import type { LibraryTopic, UserBookmark } from '@researcher/shared';
import { apiUrl } from '../config/api';

async function getHeaders(requireAuth = false): Promise<HeadersInit> {
  const auth = getAuth();
  const user = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  if (requireAuth && !token) {
    throw new Error('Authentication required');
  }

  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

// === Topics ===

export interface TopicListItem {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  category: string;
  conditionTags: string[];
  patientSummary: string;
  readTimeMinutes: number;
  lastUpdated: string;
  recentlyUpdated: boolean;
  version: number;
  sourceDocumentCount?: number;
}

export async function getTopics(options?: {
  condition?: string;
  category?: string;
  limit?: number;
  offset?: number;
}): Promise<{ topics: TopicListItem[]; hasMore: boolean }> {
  const headers = await getHeaders();
  const params = new URLSearchParams();
  if (options?.condition) params.set('condition', options.condition);
  if (options?.category) params.set('category', options.category);
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));

  const response = await fetch(`${apiUrl('/library/topics')}?${params.toString()}`, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch topics');
  }

  const data = await response.json();
  return data.data;
}

export async function getTopic(slug: string): Promise<LibraryTopic> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl(`/library/topics/${slug}`), { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Topic not found');
  }

  const data = await response.json();
  return data.data;
}

export async function searchTopics(query: string): Promise<TopicListItem[]> {
  const headers = await getHeaders();
  const params = new URLSearchParams({ q: query });
  const response = await fetch(`${apiUrl('/library/search')}?${params.toString()}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Search failed');
  }

  const data = await response.json();
  return data.data.topics;
}

// === Conditions ===

export interface ConditionItem {
  slug: string;
  label: string;
  topicCount: number;
  categories: string[];
}

export async function getConditions(): Promise<ConditionItem[]> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/library/conditions'), { headers });

  if (!response.ok) return [];

  const data = await response.json();
  return data.data;
}

// === Bookmarks ===

export async function getBookmarks(): Promise<UserBookmark[]> {
  const headers = await getHeaders(true);
  const response = await fetch(apiUrl('/library/bookmarks'), { headers });

  if (!response.ok) return [];

  const data = await response.json();
  return data.data;
}

export async function createBookmark(bookmark: {
  topicId: string;
  topicSlug: string;
  topicTitle: string;
  note?: string;
}): Promise<UserBookmark> {
  const headers = await getHeaders(true);
  const response = await fetch(apiUrl('/library/bookmarks'), {
    method: 'POST',
    headers,
    body: JSON.stringify(bookmark),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to create bookmark');
  }

  const data = await response.json();
  return data.data;
}

export async function deleteBookmark(id: string): Promise<void> {
  const headers = await getHeaders(true);
  const response = await fetch(apiUrl(`/library/bookmarks/${id}`), {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to delete bookmark');
  }
}

// === Learning Paths ===

export interface LearningPathListItem {
  id: string;
  slug: string;
  title: string;
  description: string;
  conditionTags: string[];
  difficulty: string;
  totalReadTimeMinutes: number;
  steps: { topicSlug: string; title: string; description: string; readTimeMinutes: number }[];
}

export async function getLearningPaths(): Promise<LearningPathListItem[]> {
  const headers = await getHeaders();
  const response = await fetch(apiUrl('/library/learning-paths'), { headers });

  if (!response.ok) return [];

  const data = await response.json();
  return data.data;
}

// === Topic Views (reading progress) ===

export async function recordTopicView(slug: string): Promise<void> {
  try {
    const headers = await getHeaders(true);
    await fetch(apiUrl('/library/topic-views'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ topicSlug: slug }),
    });
  } catch {
    // Fire-and-forget — don't throw
  }
}

export async function getTopicViews(): Promise<string[]> {
  const headers = await getHeaders(true);
  const response = await fetch(apiUrl('/library/topic-views'), { headers });

  if (!response.ok) return [];

  const data = await response.json();
  return data.data;
}
